import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse, HttpResponse, HttpEvent } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Globals } from 'src/app/shared/globals';
import { I18nService } from '../i18n/i18n.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(private authService: AuthService, private snackBar: MatSnackBar, private globals: Globals,
    private router: Router, private i18n: I18nService) { }

  /**
   * Tekst komunikatu statusu: najpierw slownik i18n (klucz status.NNN, tekst
   * w wybranym jezyku), a gdy klucza nie ma - stara angielska tabela w Globals.
   * Nowe kody dostaja wpisy w slownikach, starych nie ruszamy.
   */
  private statusMessage(code: string): string {
    const key = 'status.' + code;
    const translated = this.i18n.t(key);
    return translated !== key ? translated : Globals.getMessageDescription(code);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>>  {
    // Stan w chwili WYSYLANIA: 403 na zadaniu wyslanym jako "zalogowany" znaczy,
    // ze sesja wygasla (dowiadujemy sie o tym wlasnie teraz), a 403 na zadaniu
    // gościa - ze ktos naprawde probuje czegos, co wymaga zalogowania.
    const startedLoggedIn = this.authService.isLoggedIn();
    return next.handle(this.addCredentialsToRequest(request))
      .pipe(
        map(evt => {
          if (evt instanceof HttpResponse) {
            if ((evt.body) && (evt.body.status)) {
              switch (evt.body.status.type) {
                case 'E':
                  this.snackBar.open(this.statusMessage(evt.body.status.code), 'OK', { verticalPosition: 'top' });
                  break;
                case 'W':
                  this.snackBar.open(this.statusMessage(evt.body.status.code), 'OK',
                    { verticalPosition: 'top', duration: 3000 });
                  break;
                case 'I':
                  this.snackBar.open(this.statusMessage(evt.body.status.code), 'OK',
                    { verticalPosition: 'top', duration: 3000 });
              }
              return evt.clone({ body: evt.body.data });
            }
          }
          return evt;
        }),
        catchError(err => {
          if (err instanceof HttpErrorResponse) {
            switch ((err as HttpErrorResponse).status) {
              case 0:
                this.snackBar.open(Globals.getMessageDescription('000'), 'OK', { verticalPosition: 'top', duration: 5000 });
                return throwError(err);
              case 400:
                this.snackBar.open(Globals.getMessageDescription('002'), 'OK',
                  { verticalPosition: 'top', duration: 5000 });
                return throwError(err);
              case 401:
                // 401 means insufficient rights (e.g. private tree, membership level),
                // not an expired session - keep credentials and stay silent
                return throwError(err);
              case 403:
                this.authService.cleanupCredentials();
                this.authService.authStateChanged.next(false);
                // Sondy sesji (/user/me, /tree/get/currentuser) maja prawo pasc bez slowa
                if (err.url?.includes('/user/me') || err.url?.includes('/tree/get/currentuser')) {
                  return throwError(err);
                }
                if (startedLoggedIn) {
                  // Sesja wygasla, a localStorage jeszcze mowil "zalogowany" - strona
                  // glowna i inne publiczne widoki po prostu przelaczaja sie na goscia
                  // (komponenty nasluchuja authStateChanged). Bez "not authorized"
                  // i bez wyrzucania na /signin: do 2026-09-20 dwa rownolegle
                  // GET /user/tree ze strony glownej dawaly dwa czerwone komunikaty
                  // po angielsku i ekran logowania. Jeden lagodny komunikat na
                  // wygasniecie - flaga zeruje sie po ponownym zalogowaniu.
                  if (!this.authService.sessionExpiredShown) {
                    this.authService.sessionExpiredShown = true;
                    this.snackBar.open(this.i18n.t('msg.sessionExpired'), 'OK',
                      { verticalPosition: 'top', duration: 4000 });
                  }
                  return throwError(err);
                }
                // Gosc probuje operacji za logowaniem: komunikat i ekran logowania,
                // po zalogowaniu powrot tam, gdzie byl
                this.snackBar.open(this.statusMessage('006'), 'OK',
                  { verticalPosition: 'top', duration: 5000 });
                this.authService.setRedirectUrl(this.router.url);
                this.router.navigate(['/signin']);
                return throwError(err);
              case 404:
                this.snackBar.open(Globals.getMessageDescription('000'), 'OK', { verticalPosition: 'top', duration: 5000 });
                return throwError(err);
              default:
                return throwError(err);
            }
          } else {
            return throwError(err);
          }
        })
      );
  }

  /**
   * Ciasteczko sesji doklejamy WYLACZNIE do wywolan naszego backendu.
   *
   * Wczesniej withCredentials szlo do kazdego zadania, niezaleznie od domeny.
   * Dzis nic nie wycieka, bo wszystkie wywolania ida na environment.backendUrl -
   * ale pierwsze zapytanie do obcego API (geokodowanie, CDN, analityka) zaczeloby
   * wysylac tam ciasteczka sesji uzytkownika.
   */
  private addCredentialsToRequest(request: HttpRequest<unknown>): HttpRequest<unknown> {
    if (!request.url.startsWith(environment.backendUrl)) {
      return request;
    }
    return request.clone({ withCredentials: true });
  }
}
